import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import { findByAlunoCodigo } from "../api-lancamento";
import AlunoFindDialog from "../../Aluno/AlunoFindDialog";
import { ADMIN } from "../../Usuario/Perfil";
import { errorMessage } from "../../../util/ConfirmAlertUtil";
import { currencyBRL } from "../../../util/DoubleUtil";
import Container from "../../UI/Container";
import LancamentoForm from "../LancamentoForm";
import LancamentoTable, { isVencido } from "../LancamentoTable";

const FILTROS = {
  vencidos: { label: "Vencidos", test: (l) => isVencido(l) },
  abertos: { label: "Em aberto", test: (l) => !l.quitado },
  quitados: { label: "Quitados", test: (l) => l.quitado },
  todos: { label: "Todos", test: () => true },
};

const EMPTY_TEXT = {
  vencidos: "Nenhuma parcela vencida. Todas as parcelas estão em dia.",
  abertos: "Nenhuma parcela em aberto.",
  quitados: "Nenhuma parcela quitada.",
  todos: "Nenhum lançamento para este aluno.",
};

const valorDe = (l) => Number(l.valor) || 0;

/** Ordena por vencimento, do mais recente para o mais antigo. */
const porVencimento = (a, b) =>
  moment.utc(b.dataVencimento).valueOf() - moment.utc(a.dataVencimento).valueOf();

const Resumo = ({ label, value, detalhe, color }) => (
  <Grid size={{ xs: 12, sm: 4 }}>
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={600} color={color}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {detalhe}
      </Typography>
    </Paper>
  </Grid>
);

const FinanceiroInstituicaoContainer = () => {
  const usuario = useSelector((state) => state.usuario);
  const eAdmin = usuario?.perfil === ADMIN;
  const [aluno, setAluno] = useState(null);
  const [openSearch, setOpenSearch] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [lancamentos, setLancamentos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtro, setFiltro] = useState("vencidos");
  const [editando, setEditando] = useState(null);
  const codAluno = aluno?.codigo;

  useEffect(() => {
    setLancamentos([]);
    setEditando(null);
    if (!codAluno) return;
    let active = true;
    setIsLoading(true);
    findByAlunoCodigo(codAluno)
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(
            data.message === "User not Authorized."
              ? "Usuário sem autorização."
              : data.message || "Não foi possível carregar os lançamentos.",
          );
        } else if (Array.isArray(data)) {
          setLancamentos([...data].sort(porVencimento));
        }
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [codAluno]);

  const filtrados = useMemo(() => lancamentos.filter(FILTROS[filtro].test), [lancamentos, filtro]);

  const resumo = useMemo(() => {
    const vencidos = lancamentos.filter(isVencido);
    const abertos = lancamentos.filter((l) => !l.quitado);
    const quitados = lancamentos.filter((l) => l.quitado);
    return {
      vencidos: { qtd: vencidos.length, total: vencidos.reduce((s, l) => s + valorDe(l), 0) },
      abertos: { qtd: abertos.length, total: abertos.reduce((s, l) => s + valorDe(l), 0) },
      quitados: {
        qtd: quitados.length,
        total: quitados.reduce((s, l) => s + (Number(l.valorPago) || valorDe(l)), 0),
      },
    };
  }, [lancamentos]);

  const handleSaved = (data, eNovo) =>
    setLancamentos((prevState) =>
      (eNovo ? [...prevState, data] : prevState.map((l) => (l.codigo === data.codigo ? data : l))).sort(
        porVencimento,
      ),
    );

  const handleRemoved = (codigo) => setLancamentos((prevState) => prevState.filter((l) => l.codigo !== codigo));

  const parcelas = (qtd) => `${qtd} parcela${qtd === 1 ? "" : "s"}`;

  return (
    <Container>
      <AlunoFindDialog openSearch={openSearch} setOpenSearch={setOpenSearch} setAluno={setAluno} />
      <LancamentoForm
        aluno={aluno}
        lancamento={editando}
        podeRemover={eAdmin}
        onClose={() => setEditando(null)}
        onSaved={handleSaved}
        onRemoved={handleRemoved}
      />

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} mb={3}>
        <Box>
          <Typography variant="h6">Financeiro</Typography>
          <Typography variant="body2" color="text.secondary">
            Consulte e gerencie os lançamentos financeiros dos alunos.
          </Typography>
        </Box>
        <Tooltip title="Mais opções">
          <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem component={Link} to="/historico-lancamento">
            <ListItemIcon>
              <HistoryOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Histórico de Lançamento
          </MenuItem>
          <MenuItem component={Link} to="/sms-reminder-lancamento">
            <ListItemIcon>
              <NotificationsActiveOutlinedIcon fontSize="small" />
            </ListItemIcon>
            SMS Lembrete
          </MenuItem>
          <MenuItem component={Link} to="/sms-pendencia">
            <ListItemIcon>
              <SmsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            SMS Pendência
          </MenuItem>
        </Menu>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Box minWidth={0}>
            <Typography variant="caption" color="text.secondary">
              Aluno
            </Typography>
            <Typography variant="subtitle1" fontWeight={600} noWrap>
              {aluno?.nome ?? "Nenhum aluno selecionado"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<PersonSearchOutlinedIcon />} onClick={() => setOpenSearch(true)}>
              {aluno ? "Trocar Aluno" : "Buscar Aluno"}
            </Button>
            {aluno && eAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setEditando({ aluno, eNovo: true })}
              >
                Novo Lançamento
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>

      {aluno && (
        <Stack spacing={2}>
          {!isLoading && lancamentos.length > 0 && (
            <Grid container spacing={2}>
              <Resumo
                label="Vencido"
                value={currencyBRL(resumo.vencidos.total)}
                detalhe={parcelas(resumo.vencidos.qtd)}
                color={resumo.vencidos.qtd > 0 ? "error.main" : undefined}
              />
              <Resumo
                label="Em aberto"
                value={currencyBRL(resumo.abertos.total)}
                detalhe={parcelas(resumo.abertos.qtd)}
              />
              <Resumo
                label="Quitado"
                value={currencyBRL(resumo.quitados.total)}
                detalhe={parcelas(resumo.quitados.qtd)}
                color="success.main"
              />
            </Grid>
          )}

          <ToggleButtonGroup
            size="small"
            exclusive
            value={filtro}
            onChange={(event, value) => value && setFiltro(value)}
            sx={{ flexWrap: "wrap" }}
          >
            {Object.entries(FILTROS).map(([key, f]) => (
              <ToggleButton key={key} value={key}>
                {f.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <LancamentoTable
            lancamentos={filtrados}
            isLoading={isLoading}
            emptyText={EMPTY_TEXT[filtro]}
            onSelect={setEditando}
          />
        </Stack>
      )}
    </Container>
  );
};

export default FinanceiroInstituicaoContainer;
